import '../src/globals.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { setCountryRegistryLoader } from '@shared/hooks/useCountryRegistry';
import { sentryEnvironment } from '@shared/lib/sentryEnvironment';
import App from './App';
import { getCountryRegistry } from './services/api';

// Sentry — admin-frontend project (bollocorp org, EU). DSN is not secret;
// designed to be embedded in client code. Override via VITE_SENTRY_DSN.
const SENTRY_DSN =
  import.meta.env.VITE_SENTRY_DSN ||
  'https://cab17d8e869e43ea3e44b1c42dea7e7c@o4511330844278784.ingest.de.sentry.io/4511330882551888';

// Production only, decided from the hostname: the image is the same in dev
// and prod (`@shared/lib/sentryEnvironment`); dev and any other host do not report.
const SENTRY_ENV = sentryEnvironment(window.location.hostname);

if (SENTRY_DSN && SENTRY_ENV && import.meta.env.MODE !== 'test') {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV,
    release: __APP_VERSION__,  // release attribution (matches tenant frontend + both backends)
    integrations: [
      Sentry.browserTracingIntegration(),
      // v10: Replay must be in `integrations` explicitly for the
      // `replaysOnErrorSampleRate` knobs below to take effect.
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

// The platform country registry (Plataforma → Países) feeds every country
// picker, phone prefix and tax-id list. It is a lazy module-level store shared
// with the tenant SPA; each app says once how to fetch it, and nothing is
// requested until a component asks. The admin reads it through its own
// endpoint (the editor's), which already returns the effective list.
setCountryRegistryLoader(() => getCountryRegistry().then((r) => ({ countries: r.countries })));

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Could not find root element');

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div style={{ padding: 24 }}>Something went wrong. The error was reported.</div>}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
